import React, { useState } from 'react';
import LifeCycleStatusModal from './LifeCycleStatusModal';
import LifeCycleTooltip from './LifeCycleTooltip';
import { Article, LifeCycleStatus, LifeCycleChain } from '../types/api.types';
import { useLifeCycles, useLifeCycle, useLifeCycleRecommendations } from '../hooks/useLifeCycles';
import { useDatabaseContext } from '../contexts/DatabaseContext';

interface CyclesDeVieTableProps {
  articles: Article[];
  pageSize?: number;
}

const getCurrentLifeCycleStage = (lifeCycle: LifeCycleStatus | null): string => {
  if (!lifeCycle) return 'Non défini';
  return lifeCycle.current_status || 'Non défini';
};

const CyclesDeVieTable: React.FC<CyclesDeVieTableProps> = ({
  articles,
  pageSize = 10,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<keyof Article>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalArticle, setModalArticle] = useState<Article | null>(null);
  const [hoveredArticle, setHoveredArticle] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const { activeDatabase } = useDatabaseContext();
  
  const { lifeCycles, isLoading: isLoadingLifeCycles } = useLifeCycles(
    activeDatabase?.id || '', 
    articles.map(article => article.id.toString())
  );

  const { recommendations, isLoading: isLoadingRecommendations } = useLifeCycleRecommendations(
    activeDatabase?.id || '',
    articles.map(article => article.id.toString())
  );

  // Get detailed lifecycle for hovered article
  const { lifeCycle: detailedLifeCycle, isLoading: isLoadingDetailed } = useLifeCycle(
    activeDatabase?.id || '',
    hoveredArticle || ''
  );

  // Sorting logic
  const sortedArticles = [...articles].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    if (aValue === bValue) return 0;
    if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
    return aValue < bValue ? 1 : -1;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedArticles.length / pageSize);
  const paginatedArticles = sortedArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Selection logic
  const toggleSelect = (ref: string) => {
    setSelected((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ref)) newSet.delete(ref);
      else newSet.add(ref);
      return newSet;
    });
  };

  // Sorting handler
  const handleSort = (key: keyof Article) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  // Handle row hover
  const handleRowMouseEnter = (articleId: string, event: React.MouseEvent) => {
    setHoveredArticle(articleId);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleRowMouseLeave = () => {
    setHoveredArticle(null);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalArticle(null);
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 relative">
      <table className="min-w-full table-auto">
        <thead>
          <tr>
            <th></th>
            {['ref', 'nom', 'cycle'].map((key) => (
              <th
                key={key}
                className="cursor-pointer px-4 py-2"
                onClick={() => handleSort(key as keyof Article)}
              >
                {key.toUpperCase()}
                {sortBy === key && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
              </th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedArticles.map((article) => {
            const articleLifeCycle = lifeCycles?.find(
              (lc: LifeCycleStatus) => lc.article_id === article.id.toString()
            );
            const hasLifeCycle = articleLifeCycle && articleLifeCycle.current_status !== 'Non défini';
            
            return (
              <tr 
                key={article.id} 
                className="border-t hover:bg-gray-50 cursor-pointer"
                onMouseEnter={(e) => handleRowMouseEnter(article.id.toString(), e)}
                onMouseLeave={handleRowMouseLeave}
              >
                <td className="px-2 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(String(article.id))}
                    onChange={() => toggleSelect(String(article.id))}
                    className="accent-green-500"
                  />
                </td>
                <td className="px-4 py-2">{article.id}</td>
                <td className="px-4 py-2">{article.name}</td>
                <td className="px-4 py-2">
                  {isLoadingLifeCycles ? (
                    <span className="text-gray-500">Chargement...</span>
                  ) : (
                    getCurrentLifeCycleStage(articleLifeCycle)
                  )}
                </td>
                <td className="px-4 py-2">
                  <button
                    className={`px-3 py-1 rounded text-white ${
                      hasLifeCycle ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalArticle(article);
                    }}
                  >
                    {hasLifeCycle ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      {/* Tooltip */}
      {hoveredArticle && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y - 10,
          }}
        >
          <LifeCycleTooltip 
            lifeCycle={detailedLifeCycle as LifeCycleChain}
            isLoading={isLoadingDetailed}
          />
        </div>
      )}
      
      {/* Pagination controls */}
      <div className="flex justify-end items-center mt-4 gap-2">
        <button
          className="px-2 py-1 rounded border"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          Précédent
        </button>
        <span>
          Page {currentPage} / {totalPages}
        </span>
        <button
          className="px-2 py-1 rounded border"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Suivant
        </button>
      </div>
      
      {/* Modal */}
      {modalArticle && (
        <LifeCycleStatusModal
          article={modalArticle}
          onClose={handleModalClose}
          databaseId={activeDatabase?.id || ''}
          recommendation={recommendations?.[modalArticle.id.toString()]}
          isRecommendationLoading={isLoadingRecommendations}
        />
      )}
    </div>
  );
};

export default CyclesDeVieTable;
